import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import type { EOnboardingMilestone, IOnboardingTask } from '@feathr/blackbox';
import { EOnboardingTaskState } from '@feathr/blackbox';
import { Breadcrumbs, Layout, showError, Video } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import MascotAssistant from '@feathr/extender/components/MascotAssistant';
import { useOnboardingStatus } from '@feathr/extender/hooks';
import { useLocalUrl, useStore } from '@feathr/extender/state';

import { getMilestoneConfig } from './MilestonePage.types';
import MilestoneTasks from './MilestoneTasks';
import MilestoneTaskModals from './MilestoneTasks/MilestoneTaskModals';
import MilestoneTimer from './MilestoneTimer';
import useMilestoneTaskModals from './useMilestoneTaskModals';

import * as styles from './MilestonePage.css';

import mascotImage from '@feathr/extender/images/super_pixel_mascot.png';

function MilestonePage(): JSX.Element {
  const { milestone } = useParams<{ milestone: EOnboardingMilestone }>();
  const { OnboardingTasks } = useStore();
  const localUrl = useLocalUrl();
  const history = useHistory();
  const { t } = useTranslation();
  const [milestoneTasks, setMilestoneTasks] = useState<IOnboardingTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const milestoneConfig = getMilestoneConfig(milestone, t);
  const { milestone: prerequisiteMilestone, isLoading: prerequisiteMilestoneLoading } =
    useOnboardingStatus(milestoneConfig?.progressiveFeature, true);

  // Manage modal state at the page level so it can be shared
  const modalManagement = useMilestoneTaskModals();

  useEffect(() => {
    if (
      !prerequisiteMilestoneLoading &&
      prerequisiteMilestone &&
      [EOnboardingTaskState.NotStarted, EOnboardingTaskState.InProgress].includes(
        prerequisiteMilestone.state,
      )
    ) {
      history.push(localUrl('/quickstart'));
    }
  }, [prerequisiteMilestoneLoading, prerequisiteMilestone]);

  useEffect(() => {
    const fetchMilestoneTasks = async (): Promise<void> => {
      try {
        const tasks = await OnboardingTasks.getMilestoneTasks(milestone);
        setMilestoneTasks(tasks);
      } catch (error) {
        showError(error, t);
      } finally {
        setIsLoading(false);
      }
    };
    fetchMilestoneTasks();
  }, []);

  async function handleTaskStatusChange(
    taskId: string,
    newStatus: EOnboardingTaskState.InProgress | EOnboardingTaskState.Skipped,
  ): Promise<void> {
    try {
      await OnboardingTasks.updateMilestoneTask(taskId, newStatus);

      const updatedTasks = [...milestoneTasks].map((task) =>
        task.id === taskId ? { ...task, state: newStatus } : task,
      );
      setMilestoneTasks(updatedTasks);
    } catch (error) {
      showError(error, t);
    }
  }

  /**
   * @param taskId The ID of the task to mark as completed.
   * This function updates the task state to 'Completed' for the specified task ID when the user finishes a task modal.
   * It updates the local state of milestoneTasks to reflect the change but not the backend.
   *
   * It is called when the `onComplete` callback is invoked.
   */
  function markTaskCompleted(taskId: string): void {
    const updatedTasks = milestoneTasks.map((task) => {
      return task.type === taskId ? { ...task, state: EOnboardingTaskState.Completed } : task;
    });
    setMilestoneTasks(updatedTasks);
  }

  const timeRemaining = milestoneTasks
    .filter((task) => task.state !== EOnboardingTaskState.Completed && task.optional === false)
    .reduce((remaining, task) => remaining + task.minutes_to_complete, 0);

  const breadcrumbs = (
    <Breadcrumbs
      breadcrumbItems={[
        {
          key: 'back',
          label: t('Back to Milestones'),
          path: '/quickstart',
          depth: 0,
          icon: faArrowLeft,
        },
      ]}
      includeCurrentPage={true}
      theme={'link'}
    />
  );

  return (
    <Page
      BreadcrumbsComponent={prerequisiteMilestoneLoading ? <></> : breadcrumbs}
      collapseHeader={true}
      description={milestoneConfig.description}
      headerTheme={'naked'}
      loading={prerequisiteMilestoneLoading}
      title={t('Welcome to Feathr')}
    >
      <Layout
        sideContent={
          <MascotAssistant
            mascotImage={mascotImage}
            text={
              <>
                <h3>{milestoneConfig.videoHeading}</h3>
                <p>{milestoneConfig.videoDescription}</p>
                <Video
                  alt={t('Sample video')}
                  autoPlay={true}
                  openInModal={true}
                  ratio={16 / 9}
                  src={milestoneConfig.videoUrl ?? ''}
                  t={t}
                  thumbnailSrc={milestoneConfig.videoThumbnail}
                  width={1080}
                />
              </>
            }
          />
        }
        width={'full'}
      >
        <div className={styles.tasksContainer}>
          <div className={styles.header}>
            <h1 className={styles.title}>{milestoneConfig.title}</h1>
            {milestoneTasks.length > 0 && <MilestoneTimer time={timeRemaining} />}
            <div className={styles.description}>{milestoneConfig.description}</div>
          </div>

          <div className={styles.tasksContainerBody}>
            <MilestoneTasks
              isLoading={isLoading}
              milestone={milestone}
              modalManagement={modalManagement}
              onTaskStatusChange={handleTaskStatusChange}
              tasks={milestoneTasks}
            />
          </div>
        </div>
      </Layout>

      <MilestoneTaskModals modalManagement={modalManagement} onModalComplete={markTaskCompleted} />
    </Page>
  );
}

export default MilestonePage;
